import Tooltip from 'primevue/tooltip';
import type { Directive, DirectiveBinding, VNode } from 'vue';

import { EXPLANATION_CLASS, renderExplanation } from './render';
import type { ExplanationInput } from './types';

/**
 * v-explain — the house tooltip (PrimeVue `v-tooltip`) fed an Explanation.
 *
 *   <span v-explain.top="EX.effort" tabindex="0">…</span>
 *   <button v-explain="sendDisabledReason ?? EX.send" :disabled="!!sendDisabledReason">
 *
 * What it adds on top of `v-tooltip`:
 *
 *  - the card: `renderExplanation()` builds escaped HTML in the fixed order,
 *    class `explanation`, 150 ms show delay; the title defaults to the
 *    element's own label (aria-label, else its text);
 *  - FOCUS: keyboard focus opens it and blur closes it, on top of hover;
 *  - DISABLED: a native control that is `disabled` gets `aria-disabled="true"`
 *    instead. A real `disabled` button receives no mouse event and no focus,
 *    so its explanation could never open. With aria-disabled the click is
 *    swallowed (no handler runs), the look stays disabled (theme.css), and
 *    hover, focus and a TAP open the reason (E2, E6). Only native elements and
 *    component roots that are native controls — for AppButton prefer its
 *    `explanation` prop, which does the same thing explicitly;
 *  - `.tap` modifier: a click OPENS the explanation instead of closing it —
 *    for the ⓘ of <InfoTip>, the one place a touch screen has to reach it.
 *
 * A null/undefined value means "no tooltip right now" (e.g. a disabled-only
 * reason while the control is enabled): the tooltip is detached, the element
 * keeps working, and a flipped control gets its native `disabled` back.
 *
 * Never put `v-tooltip` and `v-explain` on the same element: both are the
 * PrimeVue tooltip underneath and would share its per-element state.
 */

type TooltipHook = (el: HTMLElement, binding: DirectiveBinding, vnode: VNode, prevVNode: VNode | null) => void;

type TooltipHooks = Partial<Record<
  'created' | 'beforeMount' | 'mounted' | 'beforeUpdate' | 'updated' | 'beforeUnmount' | 'unmounted',
  TooltipHook
>>;

const tooltip = Tooltip as unknown as TooltipHooks;

interface ExplainState {
  explained: boolean;
  disabledByUs: boolean;
  tap: boolean;
  onClickCapture: (event: Event) => void;
  onFocusIn: () => void;
  onFocusOut: () => void;
}

type ExplainedElement = HTMLElement & { $_explain?: ExplainState; disabled?: boolean };

/** PrimeVue reads placement (top/bottom/left/right) from modifiers; ours (`tap`) must not reach it. */
const OWN_MODIFIERS = new Set(['tap', 'focus']);

const SHOW_DELAY_MS = 150;

const labelOf = (el: HTMLElement): string =>
  el.getAttribute('aria-label')?.trim() || (el.textContent ?? '').replace(/\s+/g, ' ').trim();

const tooltipBinding = (el: HTMLElement, binding: DirectiveBinding<ExplanationInput>): DirectiveBinding => {
  const modifiers = Object.fromEntries(
    Object.entries(binding.modifiers ?? {}).filter(([key]) => !OWN_MODIFIERS.has(key)),
  );
  const value = binding.value
    ? { value: renderExplanation(binding.value, labelOf(el)), escape: false, class: EXPLANATION_CLASS, showDelay: SHOW_DELAY_MS }
    : { value: '' };

  return { ...binding, modifiers, value } as DirectiveBinding;
};

/** `disabled` as Vue would apply it: a bare attribute (`''`) or any truthy binding. */
const isDisabledProp = (value: unknown): boolean => value === '' || Boolean(value);

const supportsDisabled = (el: HTMLElement): boolean => 'disabled' in el;

const isFlipped = (el: HTMLElement): boolean => el.getAttribute('aria-disabled') === 'true';

const open = (el: HTMLElement): void => {
  el.dispatchEvent(new MouseEvent('mouseenter'));
};

const close = (el: HTMLElement): void => {
  el.dispatchEvent(new MouseEvent('mouseleave'));
};

const isKeyboardFocus = (el: HTMLElement): boolean => {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
};

/**
 * Swap native `disabled` for `aria-disabled` while an explanation exists, and
 * give `disabled` back when it goes away. Reads the DECLARED state from the
 * vnode — after the flip the element itself no longer says it is disabled.
 */
const syncDisabled = (el: ExplainedElement, vnode: VNode, explained: boolean): void => {
  const state = el.$_explain!;
  if (!supportsDisabled(el)) return;

  const declared = isDisabledProp(vnode.props?.disabled);

  if (declared && explained) {
    el.disabled = false;
    el.setAttribute('aria-disabled', 'true');
    state.disabledByUs = true;
  } else if (state.disabledByUs) {
    el.removeAttribute('aria-disabled');
    el.disabled = declared;
    state.disabledByUs = false;
  }
};

const createState = (el: ExplainedElement, tap: boolean): ExplainState => ({
  explained: false,
  disabledByUs: false,
  tap,
  /*
   * Capture phase on the element itself: at the target, capture listeners run
   * before the bubble ones, so this sees the click before Vue's @click and
   * before the tooltip's own "click hides" listener.
   */
  onClickCapture: (event: Event) => {
    const state = el.$_explain;
    if (!state?.explained) return;
    if (!isFlipped(el) && !state.tap) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    open(el);
  },
  onFocusIn: () => {
    if (el.$_explain?.explained && isKeyboardFocus(el)) open(el);
  },
  onFocusOut: () => {
    if (el.$_explain?.explained) close(el);
  },
});

/** Run the tooltip's own lifecycle — only while there IS an explanation (see `activate`). */
const callTooltip = (
  hooks: Array<keyof TooltipHooks>,
  el: HTMLElement,
  binding: DirectiveBinding<ExplanationInput>,
  vnode: VNode,
  prev: VNode | null,
): void => {
  const tooltipArgs = tooltipBinding(el, binding);
  for (const hook of hooks) tooltip[hook]?.(el, tooltipArgs, vnode, prev);
};

/*
 * The PrimeVue tooltip is only attached while the value is an Explanation. A
 * null value detaches it completely instead of leaving a disabled tooltip
 * behind: AppButton always carries v-explain, and a caller's own `v-tooltip`
 * on an unexplained AppButton must keep working alone on that element.
 */
export const vExplain: Directive<HTMLElement, ExplanationInput> = {
  beforeMount(el: ExplainedElement, binding, vnode, prev) {
    const state = createState(el, Boolean(binding.modifiers?.tap));
    state.explained = Boolean(binding.value);
    el.$_explain = state;
    el.addEventListener('click', state.onClickCapture, true);
    el.addEventListener('focusin', state.onFocusIn);
    el.addEventListener('focusout', state.onFocusOut);
    syncDisabled(el, vnode, state.explained);
    if (state.explained) callTooltip(['created', 'beforeMount'], el, binding, vnode, prev);
  },

  mounted(el: ExplainedElement, binding, vnode, prev) {
    if (el.$_explain?.explained) callTooltip(['mounted'], el, binding, vnode, prev);
  },

  updated(el: ExplainedElement, binding, vnode, prev) {
    const state = el.$_explain;
    if (!state) return;

    const wasExplained = state.explained;
    state.explained = Boolean(binding.value);
    syncDisabled(el, vnode, state.explained);

    if (state.explained && !wasExplained) {
      callTooltip(['created', 'beforeMount', 'mounted'], el, binding, vnode, prev);
    } else if (state.explained) {
      callTooltip(['updated'], el, binding, vnode, prev);
    } else if (wasExplained) {
      close(el);
      callTooltip(['beforeUnmount', 'unmounted'], el, binding, vnode, prev);
    }
  },

  beforeUnmount(el: ExplainedElement, binding, vnode, prev) {
    if (el.$_explain?.explained) callTooltip(['beforeUnmount'], el, binding, vnode, prev);
  },

  unmounted(el: ExplainedElement, binding, vnode, prev) {
    const state = el.$_explain;
    if (!state) return;

    el.removeEventListener('click', state.onClickCapture, true);
    el.removeEventListener('focusin', state.onFocusIn);
    el.removeEventListener('focusout', state.onFocusOut);
    if (state.explained) callTooltip(['unmounted'], el, binding, vnode, prev);
    delete el.$_explain;
  },
};
